import { Actor, Patch } from "@tento-lona/sheets";
import { reportWriteError } from "../../core/app/errors";
import "@tento-ui/components/input/dropdown-input";
import "@tento-ui/components/home-card/row-icon";

import { Column } from "@tento-ui/component-builtin";
import { html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { Divider } from "@tento-ui/components/divider";
import { Dropdown } from "@tento-ui/components/controls/dropdown";
import { EditableText } from "@tento-ui/components/editable-text";
import { DropdownInput } from "@tento-ui/components/input/dropdown-input";
import { RowIcon } from "@tento-ui/components/home-card/row-icon";
import { RenderPlugin } from "@tento-lona/sheets-ui";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LonaModalWebComponent } from "@tento-ui/ui/modal-component";
import { Typography } from "@tento-ui/ui/typography";
import type { RowsClient, SheetRow } from "@tento-lona";
import { Network } from "@tento-lona/network";
import { LonaModalHeader } from "@tento-ui/ui/modal-header-styles";

const TIME_SCALE_OPTIONS: { value: Network.TimeScaleCustom; label: string }[] =
  [
    { value: "year", label: "Year" },
    { value: "month", label: "Month" },
    { value: "week", label: "Week" },
    { value: "day", label: "Day" },
    { value: "custom", label: "Custom" },
  ];

@component({
  name: "lona-new-row-options-modal",
})
export class NewRowOptionsModal extends LonaModalWebComponent {
  private $headerIcon = this.hydrate<RowIcon>("header-icon");
  private $headerTitle = this.$("header-title");
  private $headerDescription = this.$("header-description");
  private $titleEditor = this.$("title-editor");
  private $timescaleContainer = this.$("timescale-container");
  private $promptContainer = this.$("prompt-container");
  private $pluginOptionsContainer = this.$("plugin-options-container");
  private $createButton = this.$("create-button");

  /**
   * The draft the form edits: a pending row (`rows.pending`), local only. It
   * never leaves this component — `onCreateRow` reads its fields and the real
   * row is minted server-side.
   */
  private pendingRow: Option<SheetRow.Pending> = null;
  private preview: Option<RenderPlugin.Preview> = null;
  private plugin: Option<RenderPlugin<any, any>> = null;
  private onCreateRow: Option<(row: SheetRow.Pending) => void> = null;

  static makeWith(
    preview: RenderPlugin.Preview,
    plugin: RenderPlugin<any, any>,
    rows: RowsClient,
    onCreateRow: (row: SheetRow.Pending) => void,
  ): NewRowOptionsModal {
    return NewRowOptionsModal.make().bind(preview, plugin, rows, onCreateRow);
  }

  constructor() {
    super();
    GESTURE_MANAGER.addPointerEvent(this.$createButton, {
      onClick: () => this.handleCreate(),
    });
  }

  bind(
    preview: RenderPlugin.Preview,
    plugin: RenderPlugin<any, any>,
    rows: RowsClient,
    onCreateRow: (row: SheetRow.Pending) => void,
  ): this {
    this.preview = preview;
    this.plugin = plugin;
    this.onCreateRow = onCreateRow;
    this.pendingRow = rows.pending(preview.id);

    // Set header content
    this.$headerTitle.textContent = preview.label;
    this.$headerDescription.textContent = preview.description;

    // Set header icon using RowIcon
    this.$headerIcon.bind({ icon: null }, { icon: preview.icon });
    this.$headerIcon.style.setProperty("--icon-hover-color", "transparent");

    // Bind title editor
    DomUtils.clearChildren(this.$titleEditor);
    const $titleInput = EditableText.make();
    $titleInput.bind(null, "Label");
    $titleInput.assignStyles({
      background: "var(--bg-surface-light)",
      paddingBlock: "12px",
      paddingInline: "8px",
      borderRadius: "8px",
    });
    $titleInput.controller.onTextChange = (t) => {
      void this.pendingRow?.update({ label: t ?? Patch.clear }, Actor.EXTERNAL).catch((e: unknown) => reportWriteError("rename the row", e));
    };
    this.$titleEditor.appendChild($titleInput);
    $titleInput.enableEditing(true);

    // Render time scale dropdown
    this.renderTimeScaleDropdown();

    // Render prompt section
    this.renderPromptSection();

    // Render plugin-specific options
    this.renderPluginOptions();

    return this;
  }

  private renderTimeScaleDropdown() {
    DomUtils.clearChildren(this.$timescaleContainer);

    const rows: Dropdown.Row<Network.TimeScaleCustom>[] =
      TIME_SCALE_OPTIONS.map((option) => ({
        content: option.label,
        data: option.value,
        selected: option.value === "month",
      }));

    const $dropdown = Dropdown.make() as Dropdown<Network.TimeScaleCustom>;

    const $dropdownInput = DropdownInput.makeWith({
      label: "Time Scale",
      currentValue: "Month",
      dropdown: $dropdown,
    });

    $dropdown.bind({
      rows,
      onSelectedRow: (row) => {
        void this.pendingRow?.update({ timeScale: row.data }, Actor.EXTERNAL)
          .catch((e: unknown) => reportWriteError("change the time scale", e));
        $dropdownInput.updateCurrentValue(row.content as string);
      },
    });

    // Select default (month)
    const defaultRow = rows.find((r) => r.data === "month");
    if (defaultRow) {
      $dropdown.bindSelectedRow(defaultRow, false);
    }

    this.$timescaleContainer.appendChild($dropdownInput);
  }

  private renderPromptSection() {
    DomUtils.clearChildren(this.$promptContainer);

    const $addPrompt = Typography.p("+ Add prompt").assignStyles({
      color: "var(--text-sec)",
      cursor: "pointer",
      "--margin-top": "0px",
      "--margin-bottom": "0px",
    });

    GESTURE_MANAGER.addPointerEvent($addPrompt, {
      onClick: () => {
        DomUtils.clearChildren(this.$promptContainer);

        const $input = EditableText.make();
        $input.bind(null, "Describe what to track...");
        $input.assignStyles({
          background: "var(--bg-surface-light)",
          paddingBlock: "12px",
          paddingInline: "8px",
          borderRadius: "8px",
        });

        $input.controller.onTextChange = (t) => {
          if (this.pendingRow) {
            const attrs = this.pendingRow.attributes ?? {};
            void this.pendingRow.update({ attributes: { ...attrs, prompt: t ?? null } }, Actor.EXTERNAL)
              .catch((e: unknown) => reportWriteError("save the prompt", e));
          }
        };

        this.$promptContainer.appendChild($input);
        $input.enableEditing(true);
      },
    });

    this.$promptContainer.appendChild($addPrompt);
  }

  private async renderPluginOptions() {
    DomUtils.clearChildren(this.$pluginOptionsContainer);

    if (!this.plugin || !this.pendingRow || !this.preview) {
      this.$pluginOptionsContainer.style.display = "none";
      return;
    }

    const makeRowOptions = this.plugin.makeRowOptions;
    if (!makeRowOptions) {
      this.$pluginOptionsContainer.style.display = "none";
      return;
    }

    const $options = await makeRowOptions({
      handle: null,
      row: this.pendingRow as any,
      dismiss: () => {},
      target: "default",
      standalone: true,
    });

    if ($options) {
      // Add divider before plugin options
      const $divider = Divider.make();
      $divider.assignStyles({
        marginBlock: "8px",
        marginInline: "0px",
        // paddingInline: "8px",
      });
      this.$pluginOptionsContainer.appendChild($divider);

      // Plugin options are wrapped in a container
      const $wrapper = Column.make();
      $wrapper.appendChild($options);
      // $wrapper.assignStyles({ paddingInline: "8px" });
      this.$pluginOptionsContainer.appendChild($wrapper);
      this.$pluginOptionsContainer.style.display = "";
    } else {
      this.$pluginOptionsContainer.style.display = "none";
    }
  }

  private handleCreate() {
    if (!this.preview || !this.onCreateRow || !this.pendingRow) return;

    this.onCreateRow(this.pendingRow);
    this.dismiss();
  }

  static $styles = [
    LonaModalWebComponent.$style,
    LonaModalHeader.$style,
    Divider.$hr,
    Button.$style,
    css`
      :host {
        --std-modal-max-width: 400px;
        --std-modal-min-height: auto;
        --std-modal-max-height: calc(100vh - 400px);
        background: var(--bg);
        border-radius: 12px;
      }

      #root {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      #header-icon {
        align-self: start;
      }

      .lona-modal-header {
        gap: 12px;
        flex-direction: row;
        align-items: center;
      }

      hr {
        margin: 0;
      }

      #section-header {
        padding: 8px 20px;
      }

      #section-label {
        --font-size: var(--caption-size);
        color: var(--text-sec);
        --margin-top: 0px;
        --margin-bottom: 0px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      #title-container {
        padding: 0 8px;
      }

      #timescale-container {
        padding: 0 12px;
      }

      #prompt-container {
        padding: 0 8px;
      }

      #plugin-options-container {
        overflow-y: auto;
      }

      #footer {
        padding: 12px 20px;
        justify-content: flex-end;
        border-top: 1px solid var(--divider);
      }

      #create-button {
        cursor: pointer;
      }

      #timescale-container {
        padding: 0px;
      }

      #content {
        margin: 12px 4px;
      }
    `,
  ];

  static $html = html`
    <std-col id="root">
      <std-row class="lona-modal-header">
        <std-row-icon id="header-icon"></std-row-icon>
        <std-col id="header-text">
          <h1 id="header-title"></h1>
          <p id="header-description"></p>
        </std-col>
      </std-row>
      <hr />
      <std-col id="content">
        <std-col style=padding-inline:8px;padding-block:4px;>
          <std-col id="title-container">
            <std-col id="title-editor"></std-col>
          </std-col>
          <std-col id="timescale-container"></std-col>
          <std-col id="prompt-container"></std-col>
        </std-col>
        <std-col id="plugin-options-container"></std-col>
      </std-col>
      <std-row id="footer">
        <button id="create-button" primary>Create</button>
      </std-row>
    </std-col>
  `;
}
